import { ICustomers } from 'state/dashboard/dashboard.types'
import { convertPaisaToRupee } from 'state/billing/billing.helpers'
import { IKeyVals } from 'state/tags/tags.types'
import {
  Account,
  GroupSpecificData,
  IBillsData,
  IOverallSalesOrder,
  ISalesAnalyticsItemLevelData,
  ISpecificGroupTableData,
  IStatementGroupTable,
  ResponseBill,
  ResponseData,
  SpecificGroupSummary
} from './supply.types'

export function formatSpecificGroupsData(data: GroupSpecificData): ISpecificGroupTableData[] {
  const { accounts } = data

  return accounts.map((account: Account) => {
    const {
      customer_code,
      id,
      name,
      summary_stats: {
        balance_due,
        total_cash_payments,
        total_neft_payments,
        total_damages,
        total_returns,
        total_other_payments,
        total_wallet_payments,
        total_cheque_payments,
        total_online_payments,
        total_payments,
        total_dues
      }
    } = account

    const invoiceDue = Number(balance_due) / 100
    const credit = Number(total_dues) / 100
    const totalPayments = Number(total_payments) / 100
    const cash = Number(total_cash_payments) / 100
    const neft = Number(total_neft_payments) / 100
    const damages = Number(total_damages) / 100
    const advanceUsed = Number(total_wallet_payments) / 100
    const returns = Number(total_returns) / 100
    const others = Number(total_other_payments) / 100
    const cheque = Number(total_cheque_payments) / 100
    const qrLink = Number(total_online_payments) / 100

    return {
      customerCode: customer_code,
      customerId: id,
      customerName: name,
      invoiceDue,
      credit,
      totalPayments,
      damages,
      cash,
      neft,
      cheque,
      advanceUsed,
      qrLink,
      returns,
      others
    }
  })
}

export function formatGroupDataHeading(accounts: Account[]): SpecificGroupSummary {
  return accounts.reduce(
    (acc: SpecificGroupSummary, account: Account) => {
      const {
        summary_stats: {
          balance_due,
          total_cash_payments,
          total_neft_payments,
          total_damages,
          total_returns,
          total_other_payments,
          total_wallet_payments,
          total_cheque_payments,
          total_online_payments,
          total_payments,
          total_dues
        }
      } = account
      acc.invoiceDue = acc.invoiceDue + Number(balance_due) / 100
      acc.credit = acc.credit + Number(total_dues) / 100
      acc.totalPayments = acc.totalPayments + Number(total_payments) / 100
      acc.damages = acc.damages + Number(total_damages) / 100
      acc.cash = acc.cash + Number(total_cash_payments) / 100
      acc.neft = acc.neft + Number(total_neft_payments) / 100
      acc.cheque = acc.cheque + Number(total_cheque_payments) / 100
      acc.advanceUsed = acc.advanceUsed + Number(total_wallet_payments) / 100
      acc.qrLink = acc.qrLink + Number(total_online_payments) / 100
      acc.returns = acc.returns + Number(total_returns) / 100
      acc.others = acc.others + Number(total_other_payments) / 100

      return acc
    },
    {
      invoiceDue: 0,
      credit: 0,
      totalPayments: 0,
      damages: 0,
      cash: 0,
      neft: 0,
      cheque: 0,
      advanceUsed: 0,
      qrLink: 0,
      returns: 0,
      others: 0
    } as SpecificGroupSummary
  )
}

export function formatStatementGroupsData(data: ResponseData): IStatementGroupTable[] {
  const { collection_lists } = data

  return collection_lists.map((collectionList) => {
    const {
      id,
      name,
      summary_stats: {
        balance_due,
        total_cash_payments,
        total_neft_payments,
        total_damages,
        total_returns,
        total_other_payments,
        total_wallet_payments,
        total_cheque_payments,
        total_online_payments,
        total_payments,
        total_dues
      }
    } = collectionList

    const invoiceDue = Number(balance_due) / 100
    const credit = Number(total_dues) / 100
    const totalPayments = Number(total_payments) / 100
    const cash = Number(total_cash_payments) / 100
    const neft = Number(total_neft_payments) / 100
    const damages = Number(total_damages) / 100
    const advanceUsed = Number(total_wallet_payments) / 100
    const returns = Number(total_returns) / 100
    const others = Number(total_other_payments) / 100
    const cheque = Number(total_cheque_payments) / 100
    const qrLink = Number(total_online_payments) / 100

    return {
      groupId: id,
      groupName: name,
      invoiceDue,
      credit,
      totalPayments,
      damages,
      cash,
      neft,
      cheque,
      advanceUsed,
      qrLink,
      returns,
      others
    }
  })
}

export function formatInvoices(bills?: ResponseBill[]): IBillsData[] {
  if (!bills) {
    return []
  }

  return bills.map((bill: ResponseBill) => ({
    invoiceNumber: bill.bill_number,
    invoiceDate: bill.bill_date,
    invoiceAmountPayable: Number(bill.bill_amount) / 100,
    invoiceAmountDue: Number(bill.bill_due_amount || 0) / 100,
    invoiceStatus: bill.status
  }))
}

export const getSalesAnalyticsWithCustomerName = (
  data: ISalesAnalyticsItemLevelData[],
  customerByIds: ICustomers['byIds'],
  accontLabels: IKeyVals[]
) => {
  return data.map((item) => {
    let updatedLabelsObject: any = {}
    Object.keys(item.labels || {}).forEach((labelId) => {
      const labelObject = accontLabels.find((label) => label.key_id === labelId)
      const labelName = labelObject?.key || ''
      const labelIdToValueMapper: any = {}
      labelObject?.vals.forEach((val) => {
        labelIdToValueMapper[val.val_id] = val.val
      })
      updatedLabelsObject = {
        ...updatedLabelsObject,
        [labelName]: item.labels?.[labelId].map(
          (labelValueId) => labelIdToValueMapper[labelValueId]
        )
      }
    })
    return {
      ...item,
      customer_name: customerByIds[item.customer_id]?.description || '',
      rate: convertPaisaToRupee(item.rate),
      invoice_amount: convertPaisaToRupee(item.invoice_amount),
      cash_amount: convertPaisaToRupee(item.cash_amount),
      cheque_amount: convertPaisaToRupee(item.cheque_amount),
      advance_amount: convertPaisaToRupee(item.advance_amount),
      qr_amount: convertPaisaToRupee(item.qr_amount),
      neft_amount: convertPaisaToRupee(item.neft_amount),
      damage_amount: convertPaisaToRupee(item.damage_amount),
      returns_amount: convertPaisaToRupee(item.returns_amount),
      others_amount: convertPaisaToRupee(item.others_amount),
      originalLabels: item.labels,
      labels: updatedLabelsObject
    }
  })
}

export const formattedSalesAnalyticsOuterData = (item: IOverallSalesOrder): IOverallSalesOrder => {
  return {
    ...item,
    total_invoice_amount: convertPaisaToRupee(item.total_invoice_amount),
    total_cash_amount: convertPaisaToRupee(item.total_cash_amount),
    total_cheque_amount: convertPaisaToRupee(item.total_cheque_amount),
    total_advance_amount: convertPaisaToRupee(item.total_advance_amount),
    total_qr_amount: convertPaisaToRupee(item.total_qr_amount),
    total_neft_amount: convertPaisaToRupee(item.total_neft_amount),
    total_damage_amount: convertPaisaToRupee(item.total_damage_amount),
    total_returns_amount: convertPaisaToRupee(item.total_returns_amount),
    total_others_amount: convertPaisaToRupee(item.total_others_amount),
    total_paid_amount: convertPaisaToRupee(item.total_paid_amount),
    group_name: 'Overall'
  }
}
